import { c as _c } from "react/compiler-runtime";
import * as RadixSelect from '@radix-ui/react-select';
import isNil from 'lodash/isNil';
import { forwardRef, useState } from 'react';
import FormLabel from '../form/internals/form-label';
import Hint from '../hint';
import SelectContent from './components/select-content';
import SelectTrigger from './components/select-trigger';
import type { SelectProps } from './select.types';
const Select = forwardRef<HTMLButtonElement, SelectProps>((t0, ref) => {
  const $ = _c(84);
  let ariaLabelProp;
  let className;
  let contentProps;
  let customFindOptionLabel;
  let customTrigger;
  let hint;
  let label;
  let onBlur;
  let onChange;
  let onFocus;
  let onOpenChange;
  let options;
  let rest;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let value;
  if ($[0] !== t0) {
    ({
      "aria-label": ariaLabelProp,
      label,
      hint,
      id: t1,
      onChange,
      value,
      options,
      contentProps,
      disabled: t2,
      className,
      size: t3,
      hasError: t4,
      placeholder: t5,
      customTrigger,
      customFindOptionLabel,
      defaultOpen: t6,
      alwaysOpen: t7,
      onBlur,
      onFocus,
      autoFocus: t8,
      onOpenChange,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = ariaLabelProp;
    $[2] = className;
    $[3] = contentProps;
    $[4] = customFindOptionLabel;
    $[5] = customTrigger;
    $[6] = hint;
    $[7] = label;
    $[8] = onBlur;
    $[9] = onChange;
    $[10] = onFocus;
    $[11] = onOpenChange;
    $[12] = options;
    $[13] = rest;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = value;
  } else {
    ariaLabelProp = $[1];
    className = $[2];
    contentProps = $[3];
    customFindOptionLabel = $[4];
    customTrigger = $[5];
    hint = $[6];
    label = $[7];
    onBlur = $[8];
    onChange = $[9];
    onFocus = $[10];
    onOpenChange = $[11];
    options = $[12];
    rest = $[13];
    t1 = $[14];
    t2 = $[15];
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
    t8 = $[21];
    value = $[22];
  }
  const id = t1 === undefined ? "select" : t1;
  const disabled = t2 === undefined ? false : t2;
  const size = t3 === undefined ? "default" : t3;
  const hasError = t4 === undefined ? false : t4;
  const placeholder = t5 === undefined ? "Select..." : t5;
  const defaultOpen = t6 === undefined ? false : t6;
  const alwaysOpen = t7 === undefined ? false : t7;
  const autoFocus = t8 === undefined ? false : t8;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  let t9;
  if ($[23] !== value) {
    t9 = value === "" || isNil(value) ? "" : String(value);
    $[23] = value;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const normalizedValue = t9;
  let t10;
  bb0: {
    if (label) {
      t10 = undefined;
      break bb0;
    }
    if (ariaLabelProp === null) {
      t10 = undefined;
      break bb0;
    }
    let t11;
    if ($[25] !== ariaLabelProp || $[26] !== normalizedValue || $[27] !== options || $[28] !== placeholder) {
      t11 = ariaLabelProp || options.find(option => option.value === normalizedValue)?.label || placeholder;
      $[25] = ariaLabelProp;
      $[26] = normalizedValue;
      $[27] = options;
      $[28] = placeholder;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    t10 = t11;
  }
  const ariaLabel = t10;
  let t11;
  if ($[30] !== options) {
    t11 = value_0 => {
      if (value_0 === "") {
        return "";
      }
      const option_1 = options.find(option_0 => option_0.value === value_0);
      return option_1?.component ?? option_1?.label ?? "";
    };
    $[30] = options;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const findOptionLabel = t11;
  let t12;
  if ($[32] !== onOpenChange) {
    t12 = open => {
      setIsOpen(open);
      onOpenChange?.(open);
    };
    $[32] = onOpenChange;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const handleOpenChange = t12;
  let t13;
  if ($[34] !== onChange) {
    t13 = value_1 => {
      onChange(value_1);
    };
    $[34] = onChange;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const handleValueChange = t13;
  let t14;
  if ($[36] !== customTrigger || $[37] !== disabled || $[38] !== isOpen) {
    t14 = () => {
      if (typeof customTrigger === "function") {
        return customTrigger({
          isOpen,
          disabled
        });
      }
      return customTrigger;
    };
    $[36] = customTrigger;
    $[37] = disabled;
    $[38] = isOpen;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const renderCustomTrigger = t14;
  let t15;
  if ($[40] !== hasError || $[41] !== id || $[42] !== label || $[43] !== size) {
    t15 = label && <FormLabel hasError={hasError} htmlFor={id} size={size}>{String(label)}</FormLabel>;
    $[40] = hasError;
    $[41] = id;
    $[42] = label;
    $[43] = size;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  const t16 = alwaysOpen || isOpen;
  let t17;
  if ($[45] !== renderCustomTrigger) {
    t17 = renderCustomTrigger();
    $[45] = renderCustomTrigger;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  const t18 = customFindOptionLabel ?? findOptionLabel;
  let t19;
  if ($[47] !== ariaLabel || $[48] !== autoFocus || $[49] !== className || $[50] !== disabled || $[51] !== hasError || $[52] !== id || $[53] !== normalizedValue || $[54] !== onBlur || $[55] !== onFocus || $[56] !== placeholder || $[57] !== ref || $[58] !== rest || $[59] !== size || $[60] !== t17 || $[61] !== t18) {
    t19 = <SelectTrigger {...rest} ref={ref} value={normalizedValue} placeholder={placeholder} disabled={disabled} hasError={hasError} size={size} className={className} ariaLabel={ariaLabel} customTrigger={t17} findOptionLabel={t18} id={id} onBlur={onBlur} onFocus={onFocus} autoFocus={autoFocus} />;
    $[47] = ariaLabel;
    $[48] = autoFocus;
    $[49] = className;
    $[50] = disabled;
    $[51] = hasError;
    $[52] = id;
    $[53] = normalizedValue;
    $[54] = onBlur;
    $[55] = onFocus;
    $[56] = placeholder;
    $[57] = ref;
    $[58] = rest;
    $[59] = size;
    $[60] = t17;
    $[61] = t18;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  let t20;
  if ($[63] !== ariaLabel || $[64] !== contentProps || $[65] !== normalizedValue || $[66] !== options || $[67] !== size) {
    t20 = <RadixSelect.Portal><SelectContent options={options} value={normalizedValue} size={size} ariaLabel={ariaLabel} contentProps={contentProps} /></RadixSelect.Portal>;
    $[63] = ariaLabel;
    $[64] = contentProps;
    $[65] = normalizedValue;
    $[66] = options;
    $[67] = size;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  let t21;
  if ($[69] !== disabled || $[70] !== handleOpenChange || $[71] !== handleValueChange || $[72] !== normalizedValue || $[73] !== t16 || $[74] !== t19 || $[75] !== t20) {
    t21 = <RadixSelect.Root value={normalizedValue} onValueChange={handleValueChange} onOpenChange={handleOpenChange} open={t16} disabled={disabled}>{t19}{t20}</RadixSelect.Root>;
    $[69] = disabled;
    $[70] = handleOpenChange;
    $[71] = handleValueChange;
    $[72] = normalizedValue;
    $[73] = t16;
    $[74] = t19;
    $[75] = t20;
    $[76] = t21;
  } else {
    t21 = $[76];
  }
  let t22;
  if ($[77] !== hasError || $[78] !== hint) {
    t22 = hint && <Hint className="m-0!" hasError={hasError}>{hint}</Hint>;
    $[77] = hasError;
    $[78] = hint;
    $[79] = t22;
  } else {
    t22 = $[79];
  }
  let t23;
  if ($[80] !== t15 || $[81] !== t21 || $[82] !== t22) {
    t23 = <div className="flex flex-col gap-2">{t15}{t21}{t22}</div>;
    $[80] = t15;
    $[81] = t21;
    $[82] = t22;
    $[83] = t23;
  } else {
    t23 = $[83];
  }
  return t23;
});
export default Select;